import React, { useCallback, useMemo, useState } from "react";
import { Link, Redirect } from "react-router-dom";
import axios from "axios";
import {
  FaCheck,
  FaCheckCircle,
  FaCheckSquare,
  FaChalkboardTeacher,
  FaCreditCard,
  FaHandHoldingUsd,
  FaHistory,
  FaLock,
  FaRupeeSign,
  FaUserGraduate,
  FaUserPlus,
} from "react-icons/fa";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import MdlSelectSingleStudent from "../dialog/MdlSelectSingleStudent";
import ModalSettlementConfirmation from "../dialog/ModalSettlementConfirmation";
import BackButton from "../ChildComponent/BackButton";
import SelectClass from "../ChildComponent/SelectClass";
import boyImage from "../img/boy.jpg";
import COLORS from "../resources/colors/COLORS";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const ADMIN_LOGIN_TYPE = process.env.REACT_APP_LOGIN_TYPE_ADMIN;
const TAG_DUE = "Due";
const TAG_NOT_PAID = "Not Paid";
const MODE_STUDENT = "student";
const MODE_CLASS = "class";

const MONTH_SHORT = {
  1: "Jan",
  2: "Feb",
  3: "Mar",
  4: "Apr",
  5: "May",
  6: "Jun",
  7: "Jul",
  8: "Aug",
  9: "Sep",
  10: "Oct",
  11: "Nov",
  12: "Dec",
};

//Created By Cursor
const formatMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });
};

//Created By Cursor
const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

//Created By Cursor
const getMonthLabel = (monthKey) => {
  const parts = String(monthKey || "").split("-");
  if (parts.length < 2) return monthKey || "—";
  const month = parseInt(parts[1], 10);
  const year = parts[0];
  const shortYear = year.length >= 2 ? year.slice(-2) : year;
  return `${MONTH_SHORT[month] || parts[1]} '${shortYear}`;
};

//Created By Cursor
const parseMonthList = (raw) =>
  String(raw || "")
    .split(",")
    .map((m) => m.trim())
    .filter(Boolean);

//Created By Cursor
const studentRelation = (gender) => {
  const g = String(gender || "").toLowerCase();
  if (g.startsWith("f") || g === "female" || g === "girl") return "D/O";
  return "S/O";
};

//Created By Cursor
const parseApplicableFee = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string" && raw && raw !== "null") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

//Created By Cursor — mirrors Android SettlementFeeAdvanceActivity.getDueFee
const filterDueFees = (feeDetails) => {
  const list = deepClone(Array.isArray(feeDetails) ? feeDetails : []);
  for (let i = list.length - 1; i >= 0; i--) {
    let flagRemove = false;
    const applicable = parseApplicableFee(list[i].applicable_fee);
    for (let j = applicable.length - 1; j >= 0; j--) {
      const status = applicable[j]?.payment_status;
      if (status !== TAG_DUE && status !== TAG_NOT_PAID) {
        applicable.splice(j, 1);
        flagRemove = true;
      }
    }
    if (flagRemove) {
      if (applicable.length === 0) {
        list.splice(i, 1);
      } else {
        list[i].applicable_fee = applicable;
      }
    } else {
      list[i].applicable_fee = applicable;
    }
  }
  return list;
};

//Created By Cursor — mirrors RvAdapterFeeSettlement.initDueFeeChecked
const initDueChecked = (dueFee) => {
  return dueFee.map((month) => {
    const applicable = parseApplicableFee(month.applicable_fee).map((fee) => ({
      ...fee,
      isChecked: "false",
      payable_amount: fee.outstanding,
    }));
    return {
      ...month,
      isChecked: "false",
      applicable_fee: applicable,
    };
  });
};

//Created By Cursor
const calcNetSettle = (dueMonths) => {
  let total = 0;
  for (const month of dueMonths) {
    if (month.isChecked !== "true") continue;
    for (const fee of parseApplicableFee(month.applicable_fee)) {
      if (fee.isChecked === "true") {
        total += Number(fee.payable_amount ?? fee.outstanding) || 0;
      }
    }
  }
  return total;
};

//Created By Cursor
const monthSubtotal = (month) => {
  let total = 0;
  for (const fee of parseApplicableFee(month.applicable_fee)) {
    if (fee.isChecked === "true") {
      total += Number(fee.payable_amount ?? fee.outstanding) || 0;
    }
  }
  return total;
};

//Created By Cursor
const monthOutstanding = (month) => {
  let total = 0;
  for (const fee of parseApplicableFee(month.applicable_fee)) {
    total += Number(fee.outstanding) || 0;
  }
  return total;
};

//Created By Cursor — sum already settled heads (payment_status Due Settled)
const calcAlreadySettled = (feeDetails) => {
  let total = 0;
  const list = Array.isArray(feeDetails) ? feeDetails : [];
  for (const month of list) {
    for (const fee of parseApplicableFee(month.applicable_fee)) {
      const status = String(fee?.payment_status || "");
      if (
        status === "Due Settled" ||
        String(fee?.settlement_status) === "2"
      ) {
        total +=
          Number(fee.amount_settled ?? fee.settled_amount ?? fee.fee_value) ||
          0;
      }
    }
  }
  return total;
};

const SettlementFeeAdvance = () => {
  const token = localStorage.getItem("token");
  const [loggedIn] = useState(token !== null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [settleMode, setSettleMode] = useState(MODE_STUDENT);
  const [isOpenStudentSelector, setIsOpenStudentSelector] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [mStudentDetails, setMStudentDetails] = useState([]);
  const [dueMonths, setDueMonths] = useState([]);
  const [allFeeDetails, setAllFeeDetails] = useState([]);
  const [noDues, setNoDues] = useState(false);

  //Created By Cursor — class settlement mode
  const [classPos, setClassPos] = useState(0);
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [classSessionId, setClassSessionId] = useState("");
  const [classFees, setClassFees] = useState([]);
  const [classFeesLoaded, setClassFeesLoaded] = useState(false);
  const [classLoading, setClassLoading] = useState(false);

  const student = mStudentDetails[0] || null;
  const cls = student?.class?.[0] || {};
  const netSettleAmount = useMemo(() => calcNetSettle(dueMonths), [dueMonths]);
  const hasSelection = netSettleAmount > 0;

  const selectedFeeCount = useMemo(() => {
    let count = 0;
    for (const month of dueMonths) {
      for (const fee of parseApplicableFee(month.applicable_fee)) {
        if (fee.isChecked === "true") count += 1;
      }
    }
    return count;
  }, [dueMonths]);

  const totalOutstanding = useMemo(() => {
    return dueMonths.reduce((sum, month) => sum + monthOutstanding(month), 0);
  }, [dueMonths]);

  const alreadySettledAmount = useMemo(
    () => calcAlreadySettled(allFeeDetails),
    [allFeeDetails]
  );

  //Created By Cursor
  const classSelectedFees = useMemo(
    () => classFees.filter((f) => f.isChecked === "true"),
    [classFees]
  );

  //Created By Cursor
  const classSelectedMonthCount = useMemo(() => {
    let count = 0;
    for (const fee of classSelectedFees) {
      count += parseMonthList(fee.selectedMonth).length;
    }
    return count;
  }, [classSelectedFees]);

  //Created By Cursor — estimate class settle total (fee_value × selected months)
  const classNetAmount = useMemo(() => {
    let total = 0;
    for (const fee of classSelectedFees) {
      const months = parseMonthList(fee.selectedMonth);
      const value = Number(fee.fee_value) || 0;
      total += value * months.length;
    }
    return total;
  }, [classSelectedFees]);

  const hasClassSelection =
    classSelectedFees.length > 0 &&
    classSelectedFees.every((f) => parseMonthList(f.selectedMonth).length > 0);

  const confirmAmount =
    settleMode === MODE_CLASS ? classNetAmount : netSettleAmount;

  //Created By Cursor
  const loadFeeDetails = useCallback((enrollment_no, session_id) => {
    if (!enrollment_no || !session_id) return;
    setLoading(true);
    setDueMonths([]);
    setAllFeeDetails([]);
    setNoDues(false);
    setMStudentDetails([]);

    axios
      .get(`${API_BASE()}getStudentFeeDetailsNew.php`, {
        params: {
          school_code: SessionManager.getSchoolCode(),
          session_id,
          enrollment_no,
          aadhar_no: "",
          request_type: "CURRENT",
        },
      })
      .then((res) => res.data)
      .then((response) => {
        setLoading(false);
        if (response.api_status !== "OK") {
          myToast.notify_warning(response.api_remark || "No student found.");
          return;
        }
        const data = Array.isArray(response.data) ? response.data : [];
        if (!data.length) {
          myToast.notify_warning("No student found.");
          return;
        }
        setMStudentDetails(data);
        const feeDetails = Array.isArray(data[0]?.fee_details)
          ? data[0].fee_details
          : [];
        setAllFeeDetails(feeDetails);
        const due = initDueChecked(filterDueFees(feeDetails));
        setDueMonths(due);
        setNoDues(due.length === 0);
      })
      .catch((error) => {
        setLoading(false);
        console.error("[SettlementFeeAdvance] getStudentFeeDetailsNew", error);
        myToast.notify_warning("Failed to load fee details.");
      });
  }, []);

  //Created By Cursor
  const handleSelectedStudent = useCallback(
    (student_id, _selected, session_id) => {
      setIsOpenStudentSelector(false);
      loadFeeDetails(student_id, session_id);
    },
    [loadFeeDetails]
  );

  //Created By Cursor
  const resetStudentState = useCallback(() => {
    setMStudentDetails([]);
    setDueMonths([]);
    setAllFeeDetails([]);
    setNoDues(false);
  }, []);

  //Created By Cursor
  const resetClassState = useCallback(() => {
    setClassFees([]);
    setClassFeesLoaded(false);
    setClassId("");
    setSectionId("");
    setClassSessionId("");
  }, []);

  //Created By Cursor
  const switchMode = useCallback(
    (mode) => {
      //Created By Cursor — Select Class mode is disabled
      if (mode === MODE_CLASS) return;
      if (mode === settleMode) return;
      setSettleMode(mode);
      setShowConfirm(false);
      if (mode === MODE_STUDENT) {
        resetClassState();
      } else {
        resetStudentState();
      }
    },
    [settleMode, resetClassState, resetStudentState]
  );

  //Created By Cursor — load class fees for settlement
  const loadClassFees = useCallback((class_id, session_id) => {
    if (!class_id || !session_id) return;
    setClassLoading(true);
    setClassFees([]);
    setClassFeesLoaded(false);
    axios
      .get(`${API_BASE()}getClassApplicableFee.php`, {
        params: {
          school_code: SessionManager.getSchoolCode(),
          class_id,
          session_id,
        },
      })
      .then((res) => res.data)
      .then((response) => {
        setClassLoading(false);
        setClassFeesLoaded(true);
        if (response.api_status !== "OK") {
          setClassFees([]);
          myToast.notify_warning(response.api_remark || "No class fees found.");
          return;
        }
        const list = Array.isArray(response.data) ? response.data : [];
        setClassFees(
          list.map((fee) => ({
            ...fee,
            isChecked: "false",
            selectedMonth: "",
            months: parseMonthList(fee.applicable_month),
          }))
        );
      })
      .catch((error) => {
        setClassLoading(false);
        setClassFeesLoaded(true);
        setClassFees([]);
        console.error("[SettlementFeeAdvance] getClassApplicableFee", error);
        myToast.notify_warning("Failed to load class fees.");
      });
  }, []);

  //Created By Cursor
  const handleClassSelect = useCallback(
    (
      class_id,
      section_id,
      session_id,
      selected_position
    ) => {
      setClassId(class_id || "");
      setSectionId(section_id || "");
      setClassSessionId(session_id || "");
      setClassPos(selected_position || 0);
      if (class_id && session_id) {
        loadClassFees(class_id, session_id);
      }
    },
    [loadClassFees]
  );

  //Created By Cursor — fee toggle; clearing fee locks/clears months
  const toggleClassFee = useCallback((feeIndex) => {
    setClassFees((prev) => {
      const next = deepClone(prev);
      const fee = next[feeIndex];
      if (!fee) return prev;
      const checked = fee.isChecked === "true";
      fee.isChecked = checked ? "false" : "true";
      if (checked) {
        fee.selectedMonth = "";
      }
      return next;
    });
  }, []);

  //Created By Cursor — months locked until fee is selected
  const toggleClassMonth = useCallback((feeIndex, month) => {
    setClassFees((prev) => {
      const next = deepClone(prev);
      const fee = next[feeIndex];
      if (!fee || fee.isChecked !== "true") return prev;
      const selected = new Set(parseMonthList(fee.selectedMonth));
      if (selected.has(month)) selected.delete(month);
      else selected.add(month);
      const months = Array.from(selected);
      fee.selectedMonth = months.join(",");
      if (months.length === 0) {
        fee.isChecked = "false";
      }
      return next;
    });
  }, []);

  //Created By Cursor
  const clearClassSelection = useCallback(() => {
    setClassFees((prev) =>
      deepClone(prev).map((fee) => ({
        ...fee,
        isChecked: "false",
        selectedMonth: "",
      }))
    );
  }, []);

  //Created By Cursor
  const toggleMonth = useCallback((monthIndex) => {
    setDueMonths((prev) => {
      const next = deepClone(prev);
      const month = next[monthIndex];
      if (!month) return prev;
      const checked = month.isChecked === "true";
      month.isChecked = checked ? "false" : "true";
      month.applicable_fee = parseApplicableFee(month.applicable_fee).map(
        (fee) => ({
          ...fee,
          isChecked: month.isChecked,
          payable_amount: fee.outstanding,
        })
      );
      return next;
    });
  }, []);

  //Created By Cursor
  const toggleFee = useCallback((monthIndex, feeIndex) => {
    setDueMonths((prev) => {
      const next = deepClone(prev);
      const month = next[monthIndex];
      if (!month) return prev;
      const fees = parseApplicableFee(month.applicable_fee);
      const fee = fees[feeIndex];
      if (!fee) return prev;
      const checked = fee.isChecked === "true";
      fee.isChecked = checked ? "false" : "true";
      fee.payable_amount = fee.outstanding;
      fees[feeIndex] = fee;
      month.applicable_fee = fees;
      month.isChecked = fees.some((f) => f.isChecked === "true")
        ? "true"
        : "false";
      return next;
    });
  }, []);

  //Created By Cursor
  const selectAllDues = useCallback(() => {
    setDueMonths((prev) =>
      deepClone(prev).map((month) => ({
        ...month,
        isChecked: "true",
        applicable_fee: parseApplicableFee(month.applicable_fee).map((fee) => ({
          ...fee,
          isChecked: "true",
          payable_amount: fee.outstanding,
        })),
      }))
    );
  }, []);

  //Created By Cursor
  const clearSelection = useCallback(() => {
    setDueMonths((prev) =>
      deepClone(prev).map((month) => ({
        ...month,
        isChecked: "false",
        applicable_fee: parseApplicableFee(month.applicable_fee).map((fee) => ({
          ...fee,
          isChecked: "false",
          payable_amount: fee.outstanding,
        })),
      }))
    );
  }, []);

  //Created By Cursor
  const handleSettleClick = useCallback(() => {
    if (settleMode === MODE_CLASS) {
      if (!classSelectedFees.length) {
        myToast.notify_warning("Select any fee !!!");
        return;
      }
      const missingMonth = classSelectedFees.some(
        (f) => parseMonthList(f.selectedMonth).length === 0
      );
      if (missingMonth) {
        myToast.notify_warning("Select month !!!");
        return;
      }
      setShowConfirm(true);
      return;
    }
    if (!hasSelection) {
      myToast.notify_warning("Select at least one fee to settle.");
      return;
    }
    setShowConfirm(true);
  }, [settleMode, classSelectedFees, hasSelection]);

  //Created By Cursor — class-wise settlement (mirrors FeeSettlementClassActivity)
  const submitClassSettlement = useCallback(() => {
    setShowConfirm(false);
    if (!classId || !classSessionId) {
      myToast.notify_warning("Select a class first.");
      return;
    }
    const selected = deepClone(classFees).filter((f) => f.isChecked === "true");
    if (!selected.length) {
      myToast.notify_warning("Select any fee !!!");
      return;
    }
    for (const fee of selected) {
      if (parseMonthList(fee.selectedMonth).length === 0) {
        myToast.notify_warning("Select month !!!");
        return;
      }
    }

    const staff = SessionManager.getStaffDetails() || {};
    const isAdmin =
      String(staff.login_type || "") === String(ADMIN_LOGIN_TYPE || "Admin");
    const settlement_status = isAdmin ? "2" : "1";
    const approved_by = isAdmin ? staff.staff_id || "" : "";

    setSubmitting(true);
    axios
      .get(`${API_BASE()}AddClassFeeSettlementRequest.php`, {
        params: {
          class_id: classId,
          section_id: sectionId || "",
          session_id: classSessionId,
          staff_id: staff.staff_id || "",
          fee_details: JSON.stringify(selected),
          remark: "Class fee settlement",
          settlement_status,
          approved_by,
          school_code: SessionManager.getSchoolCode(),
        },
      })
      .then((res) => res.data)
      .then((response) => {
        setSubmitting(false);
        if (response.api_status === "OK") {
          myToast.notify_success("Submitted Successfully");
          loadClassFees(classId, classSessionId);
        } else {
          myToast.notify_warning(
            response.api_remark || "Something went wrong."
          );
        }
      })
      .catch((error) => {
        setSubmitting(false);
        console.error("[SettlementFeeAdvance] class settle", error);
        myToast.notify_warning("Failed to submit class settlement.");
      });
  }, [classId, sectionId, classSessionId, classFees, loadClassFees]);

  //Created By Cursor — mirrors SettlementFeeAdvanceActivity.SettleFee
  const submitSettlement = useCallback(() => {
    if (settleMode === MODE_CLASS) {
      submitClassSettlement();
      return;
    }
    setShowConfirm(false);
    if (!student?.class?.[0]) {
      myToast.notify_warning("Student class details missing.");
      return;
    }

    const staff = SessionManager.getStaffDetails() || {};
    const selected = deepClone(dueMonths).filter((m) => m.isChecked === "true");
    if (!selected.length) {
      myToast.notify_warning("Select at least one fee to settle.");
      return;
    }

    for (let i = 0; i < selected.length; i++) {
      selected[i].applicable_fee = JSON.stringify(
        parseApplicableFee(selected[i].applicable_fee)
      );
    }

    const isAdmin =
      String(staff.login_type || "") === String(ADMIN_LOGIN_TYPE || "Admin");
    const endpoint = isAdmin ? "SettleFee.php" : "AddFeeSettlementRequest.php";

    const params = new URLSearchParams();
    params.append("student_id", student.student_enrollment_no);
    params.append("class_id", cls.class_id);
    params.append("section_id", cls.section_id || "");
    params.append("session_id", cls.session_id);
    params.append("staff_id", staff.staff_id || "");
    params.append("fee_details", JSON.stringify(selected));
    params.append("remark", "Fee payment");
    params.append("amount", String(netSettleAmount));
    params.append("school_code", SessionManager.getSchoolCode());

    setSubmitting(true);
    axios
      .post(`${API_BASE()}${endpoint}`, params)
      .then((res) => res.data)
      .then((response) => {
        setSubmitting(false);
        if (response.api_status === "OK") {
          myToast.notify_success("Submitted Successfully");
          loadFeeDetails(student.student_enrollment_no, cls.session_id);
        } else {
          myToast.notify_warning(
            response.api_remark || "Something went wrong."
          );
        }
      })
      .catch((error) => {
        setSubmitting(false);
        console.error("[SettlementFeeAdvance] settle", error);
        myToast.notify_warning("Failed to submit settlement.");
      });
  }, [
    settleMode,
    submitClassSettlement,
    student,
    cls.class_id,
    cls.section_id,
    cls.session_id,
    dueMonths,
    netSettleAmount,
    loadFeeDetails,
  ]);

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  if (loading || submitting) {
    return <MyLoader />;
  }

  const enroll = student?.student_enrollment_no || "";
  const sessionId = cls.session_id || "";
  const sessionName = cls.session_name || "";
  const classLabel =
    cls.class_name || cls.section_name
      ? `${cls.class_name || "—"}${
          cls.section_name && cls.section_name !== "null"
            ? `:${cls.section_name}`
            : ""
        }`
      : "—";

  return (
    <div
      className="content payroll-page sm-page tr-page trep-page stl-page sfa-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Settle Fee / Due</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaHandHoldingUsd />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Settle Fee or Due</h1>
              <p className="sm-hero__sub">
                Choose student or class, pick fees &amp; months, settle in one
                step
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            {settleMode === MODE_STUDENT ? (
              <button
                type="button"
                className="sfa-hero-btn"
                onClick={() => setIsOpenStudentSelector(true)}
              >
                <FaUserPlus aria-hidden />
                <span>{student ? "Change Student" : "Select Student"}</span>
              </button>
            ) : null}
          </div>
        </header>

        {/* Created By Cursor — Student / Class mode switch */}
        <div className="sfa-mode-tabs" role="tablist" aria-label="Settlement mode">
          <button
            type="button"
            role="tab"
            aria-selected={settleMode === MODE_STUDENT}
            className={`sfa-mode-tab${
              settleMode === MODE_STUDENT ? " is-active" : ""
            }`}
            onClick={() => switchMode(MODE_STUDENT)}
          >
            <FaUserGraduate aria-hidden />
            Select Student
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={settleMode === MODE_CLASS}
            aria-disabled="true"
            disabled
            title="Select Class is currently disabled"
            className={`sfa-mode-tab sfa-mode-tab--disabled${
              settleMode === MODE_CLASS ? " is-active" : ""
            }`}
          >
            <FaChalkboardTeacher aria-hidden />
            Select Class
          </button>
        </div>

        <div className="stl-body sfa-body">
          {settleMode === MODE_CLASS ? (
            <>
              <section className="trep-panel" aria-label="Select class">
                <SelectClass
                  action={handleClassSelect}
                  selected_position={classPos}
                />
              </section>

              {classLoading ? (
                <div className="tr-loading">
                  <MyLoader />
                </div>
              ) : !classId ? (
                <section className="trep-panel sfa-empty" aria-label="Pick class">
                  <div className="sfa-empty__orb" aria-hidden>
                    <FaChalkboardTeacher />
                  </div>
                  <h2 className="sfa-empty__title">Select a class</h2>
                  <p className="sfa-empty__sub">
                    Choose session, class and section above to load applicable
                    fees for settlement.
                  </p>
                </section>
              ) : classFeesLoaded && classFees.length === 0 ? (
                <section className="trep-panel sfa-empty" aria-label="No fees">
                  <div className="sfa-empty__orb" aria-hidden>
                    <FaCheckCircle />
                  </div>
                  <h2 className="sfa-empty__title">No class fees</h2>
                  <p className="sfa-empty__sub">
                    This class has no applicable fees configured for settlement.
                  </p>
                </section>
              ) : classFees.length > 0 ? (
                <>
                  <section
                    className="trep-panel sfa-dues"
                    aria-label="Class fees"
                  >
                    <div className="trep-panel__head">
                      <div className="min-w-0">
                        <h2 className="trep-panel__title">
                          <FaCheckSquare aria-hidden /> Fees &amp; months
                        </h2>
                        <p className="trep-panel__sub">
                          Select a fee first — months stay locked until the fee
                          is selected
                        </p>
                      </div>
                      <div className="sfa-dues__tools">
                        <span className="sfa-selected">
                          <span className="sfa-selected__label">Est.</span>
                          <span className="sfa-selected__value">
                            ₹{formatMoney(classNetAmount)}
                          </span>
                        </span>
                        <button
                          type="button"
                          className="sfa-text-btn"
                          onClick={clearClassSelection}
                          disabled={!classSelectedFees.length}
                        >
                          Clear
                        </button>
                      </div>
                    </div>

                    <div className="sfa-class-fee-list">
                      {classFees.map((fee, fi) => {
                        const feeOn = fee.isChecked === "true";
                        const selected = new Set(
                          parseMonthList(fee.selectedMonth)
                        );
                        const months = Array.isArray(fee.months)
                          ? fee.months
                          : parseMonthList(fee.applicable_month);
                        return (
                          <article
                            key={`${fee.fee_id || fi}`}
                            className={`sfa-class-fee${
                              feeOn ? " is-checked" : ""
                            }`}
                          >
                            <label className="sfa-class-fee__head">
                              <span
                                className={`sfa-check${feeOn ? " is-on" : ""}`}
                                aria-hidden
                              >
                                {feeOn ? <FaCheckCircle /> : null}
                              </span>
                              <input
                                type="checkbox"
                                className="sfa-sr-only"
                                checked={feeOn}
                                onChange={() => toggleClassFee(fi)}
                              />
                              <span className="sfa-class-fee__name">
                                {fee.fee_name || "Fee"}
                              </span>
                              <span className="sfa-class-fee__mode">
                                {fee.mode || "—"}
                              </span>
                              <span className="sfa-class-fee__amt">
                                ₹{formatMoney(fee.fee_value)}
                              </span>
                            </label>

                            <div
                              className={`sfa-month-grid${
                                feeOn ? "" : " is-locked"
                              }`}
                              aria-label={
                                feeOn
                                  ? "Select months"
                                  : "Months locked — select fee first"
                              }
                            >
                              {!feeOn ? (
                                <p className="sfa-month-lock-hint">
                                  <FaLock aria-hidden /> Select fee to unlock
                                  months
                                </p>
                              ) : null}
                              {months.map((month) => {
                                const on = selected.has(month);
                                return (
                                  <button
                                    key={month}
                                    type="button"
                                    disabled={!feeOn}
                                    className={`sfa-month-chip${
                                      on ? " is-on" : ""
                                    }${!feeOn ? " is-locked" : ""}`}
                                    onClick={() => toggleClassMonth(fi, month)}
                                    title={
                                      feeOn
                                        ? month
                                        : "Select fee to unlock this month"
                                    }
                                  >
                                    {!feeOn ? (
                                      <FaLock
                                        className="sfa-month-chip__lock"
                                        aria-hidden
                                      />
                                    ) : null}
                                    <span className="sfa-month-chip__label">
                                      {getMonthLabel(month)}
                                    </span>
                                    <span className="sfa-month-chip__key">
                                      {month}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </section>

                  <div className="sfa-footer">
                    <div className="sfa-footer__meta">
                      <span className="sfa-footer__label">
                        Class settle estimate
                      </span>
                      <span className="sfa-footer__amount">
                        ₹{formatMoney(classNetAmount)}
                      </span>
                      <span className="sfa-footer__hint">
                        {hasClassSelection
                          ? `${classSelectedFees.length} fee${
                              classSelectedFees.length === 1 ? "" : "s"
                            } · ${classSelectedMonthCount} month${
                              classSelectedMonthCount === 1 ? "" : "s"
                            }`
                          : "Select fee and months to continue"}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="sfa-cta sfa-cta--settle"
                      disabled={!hasClassSelection}
                      onClick={handleSettleClick}
                    >
                      <FaHandHoldingUsd aria-hidden />
                      Settle Now
                    </button>
                  </div>
                </>
              ) : null}
            </>
          ) : !student ? (
            <section className="trep-panel sfa-empty" aria-label="Select student">
              <div className="sfa-empty__orb" aria-hidden>
                <FaUserGraduate />
              </div>
              <h2 className="sfa-empty__title">Start with a student</h2>
              <p className="sfa-empty__sub">
                Search or pick from the list to load outstanding dues for
                settlement.
              </p>
              <button
                type="button"
                className="sfa-cta"
                onClick={() => setIsOpenStudentSelector(true)}
              >
                <FaUserPlus aria-hidden />
                Select Student
              </button>
            </section>
          ) : (
            <>
              <section className="trep-panel sfa-student" aria-label="Student">
                <div className="sfa-student__identity">
                  <div className="sfa-student__avatar-wrap">
                    <img
                      className="sfa-student__photo"
                      src={student.student_img_path || boyImage}
                      alt=""
                      onError={(e) => {
                        e.currentTarget.src = boyImage;
                      }}
                    />
                  </div>
                  <div className="sfa-student__main">
                    <span className="sfa-student__enroll">{enroll}</span>
                    <h2 className="sfa-student__name">{student.stu_name}</h2>
                    <p className="sfa-student__relation">
                      {studentRelation(student.gender)}:{" "}
                      {student.father_name || "—"}
                    </p>
                    <div className="sfa-student__chips">
                      <span className="sfa-chip">{classLabel}</span>
                      <span className="sfa-chip">
                        Roll {cls.student_roll_no || "—"}
                      </span>
                      <span className="sfa-chip">
                        {cls.board_name || "Board —"}
                      </span>
                      <span className="sfa-chip">
                        {cls.medium_name || "Medium —"}
                      </span>
                      <span className="sfa-chip sfa-chip--accent">
                        {sessionName || "Session —"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="sfa-student__links">
                  <Link
                    to={`/AdminMain/FeeCard/${enroll}/${sessionId}`}
                    className="sfa-link-btn"
                  >
                    <FaCreditCard aria-hidden />
                    Fee Card
                  </Link>
                  <Link
                    to={`/AdminMain/PaymentHistory/${enroll}/${sessionId}/${encodeURIComponent(
                      sessionName || "Session"
                    )}`}
                    className="sfa-link-btn"
                  >
                    <FaHistory aria-hidden />
                    History
                  </Link>
                </div>
              </section>

              {noDues ? (
                <section className="trep-panel sfa-empty sfa-empty--ok" aria-label="No dues">
                  <div className="sfa-empty__orb sfa-empty__orb--ok" aria-hidden>
                    <FaCheckCircle />
                  </div>
                  <h2 className="sfa-empty__title">No fee due</h2>
                  <p className="sfa-empty__sub">
                    This student has no outstanding dues ready for settlement.
                  </p>
                </section>
              ) : (
                <>
                  <div className="sm-kpi-row sfa-kpi" aria-label="Due summary">
                    <div className="sm-kpi-chip sm-kpi-chip--sand">
                      <span className="sm-kpi-chip__icon" aria-hidden>
                        <FaCheckSquare />
                      </span>
                      <div className="sm-kpi-chip__text">
                        <span className="sm-kpi-chip__label">Due months</span>
                        <span className="sm-kpi-chip__value">
                          {dueMonths.length}
                        </span>
                      </div>
                    </div>
                    <div className="sm-kpi-chip sm-kpi-chip--sky">
                      <span className="sm-kpi-chip__icon" aria-hidden>
                        <FaRupeeSign />
                      </span>
                      <div className="sm-kpi-chip__text">
                        <span className="sm-kpi-chip__label">Outstanding</span>
                        <span className="sm-kpi-chip__value">
                          ₹{formatMoney(totalOutstanding)}
                        </span>
                      </div>
                    </div>
                    <div className="sm-kpi-chip sm-kpi-chip--mint">
                      <span className="sm-kpi-chip__icon" aria-hidden>
                        <FaHandHoldingUsd />
                      </span>
                      <div className="sm-kpi-chip__text">
                        <span className="sm-kpi-chip__label">Settled</span>
                        <span className="sm-kpi-chip__value">
                          ₹{formatMoney(alreadySettledAmount)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <section className="trep-panel sfa-dues" aria-label="Due fees">
                    <div className="trep-panel__head">
                      <div className="min-w-0">
                        <h2 className="trep-panel__title">
                          <FaCheckSquare aria-hidden /> Due fees
                        </h2>
                        <p className="trep-panel__sub">
                          Tick a month or individual fee heads to settle
                        </p>
                      </div>
                      <div className="sfa-dues__tools">
                        <span
                          className="sfa-selected"
                          title="Amount currently selected"
                        >
                          <span className="sfa-selected__label">Selected</span>
                          <span className="sfa-selected__value">
                            ₹{formatMoney(netSettleAmount)}
                          </span>
                        </span>
                        <button
                          type="button"
                          className="sfa-text-btn"
                          onClick={selectAllDues}
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          className="sfa-text-btn"
                          onClick={clearSelection}
                          disabled={!hasSelection}
                        >
                          Clear
                        </button>
                      </div>
                    </div>

                    <div className="sfa-fee-list">
                      {dueMonths.map((month, mi) => {
                        const fees = parseApplicableFee(month.applicable_fee);
                        const monthOn = month.isChecked === "true";
                        const sub = monthSubtotal(month);
                        const outstanding = monthOutstanding(month);
                        return (
                          <article
                            key={`${month.month || mi}`}
                            className={`sfa-month${monthOn ? " is-checked" : ""}`}
                          >
                            <label className="sfa-month__head">
                              <span
                                className={`sfa-check${monthOn ? " is-on" : ""}`}
                                aria-hidden
                              >
                                {monthOn ? <FaCheckCircle /> : null}
                              </span>
                              <input
                                type="checkbox"
                                className="sfa-sr-only"
                                checked={monthOn}
                                onChange={() => toggleMonth(mi)}
                              />
                              <span className="sfa-month__label">
                                {month.month2 || month.month || "Month"}
                              </span>
                              <span className="sfa-month__count">
                                {fees.length} fee
                                {fees.length === 1 ? "" : "s"}
                              </span>
                              <span className="sfa-month__subtotal">
                                {monthOn
                                  ? `₹${formatMoney(sub)}`
                                  : `₹${formatMoney(outstanding)}`}
                              </span>
                            </label>
                            <div className="sfa-fee-rows">
                              {fees.map((fee, fi) => {
                                const feeOn = fee.isChecked === "true";
                                return (
                                  <label
                                    key={`${fee.fee_id || fi}`}
                                    className={`sfa-fee${feeOn ? " is-checked" : ""}`}
                                  >
                                    <span
                                      className={`sfa-fee-box${
                                        feeOn ? " is-on" : ""
                                      }`}
                                      aria-hidden
                                    >
                                      {feeOn ? <FaCheck /> : null}
                                    </span>
                                    <input
                                      type="checkbox"
                                      className="sfa-sr-only"
                                      checked={feeOn}
                                      onChange={() => toggleFee(mi, fi)}
                                    />
                                    <span className="sfa-fee__name">
                                      {fee.fee_name || "Fee"}
                                    </span>
                                    <span
                                      className={`sfa-fee__badge${
                                        fee.payment_status === TAG_NOT_PAID
                                          ? " sfa-fee__badge--warn"
                                          : ""
                                      }`}
                                    >
                                      {fee.payment_status || "Due"}
                                    </span>
                                    <span className="sfa-fee__amt">
                                      ₹{formatMoney(fee.outstanding)}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </section>

                  <div className="sfa-footer">
                    <div className="sfa-footer__meta">
                      <span className="sfa-footer__label">Net settle amount</span>
                      <span className="sfa-footer__amount">
                        ₹{formatMoney(netSettleAmount)}
                      </span>
                      <span className="sfa-footer__hint">
                        {selectedFeeCount
                          ? `${selectedFeeCount} fee head${
                              selectedFeeCount === 1 ? "" : "s"
                            } selected`
                          : "Select dues above to continue"}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="sfa-cta sfa-cta--settle"
                      disabled={!hasSelection}
                      onClick={handleSettleClick}
                    >
                      <FaHandHoldingUsd aria-hidden />
                      Settle Now
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {isOpenStudentSelector ? (
        <MdlSelectSingleStudent
          action={() => setIsOpenStudentSelector(false)}
          action2={handleSelectedStudent}
        />
      ) : null}

      {showConfirm ? (
        <ModalSettlementConfirmation
          netPayableAmount={confirmAmount}
          action={() => setShowConfirm(false)}
          action2={submitSettlement}
        />
      ) : null}
    </div>
  );
};

export default SettlementFeeAdvance;
